"use client";
import { OrderType } from "@/constants/enum";
import { DayPart } from "@/dto/dayPartMenu";
import { Hours } from "@/dto/hours";
import { Merchant } from "@/dto/merchant";
import { OrderEstimates } from "@/dto/orderEstimates";
import {
  ScheduleDateType,
  ScheduledDateCalendarOptions,
  ScheduleTimeType,
} from "@/dto/scheduledTime";
import { toNativeDate } from "@/utils/dateTimeUtils";
import {
  createSchedulingContext,
  getSchedulableDateCalendarOptions,
  getSchedulableDateOptions,
  getTimeOptionsForDate,
} from "@/utils/scheduleOrderUtils";
import { DateTime } from "luxon";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo } from "react";

export type ScheduledDropdownEstimateContext = Partial<
  Pick<OrderEstimates, "deliveryTime" | "pickupTime">
>;

interface UseScheduledDropdownOptionsParams {
  scheduleDateRange: number;
  businessHours?: Hours | null;
  merchant: Merchant;
  selectedDayPart?: DayPart | null;
  orderType?: OrderType | string;
  orderEstimates?: ScheduledDropdownEstimateContext;
  scheduleDate: ScheduleDateType;
  scheduleTime: ScheduleTimeType;
  setScheduleDate: (newDate: ScheduleDateType) => void;
  setScheduleTime: (newTime: ScheduleTimeType) => void;
}

/**
 * Return contract for `useScheduledDropdownOptions`.
 */
export interface UseScheduledDropdownOptionsResult {
  dateOptions: ScheduleDateType[];
  /** Same schedulability verdict as `dateOptions`, shaped for the Advanced Ordering calendar. */
  calendarOptions: ScheduledDateCalendarOptions;
  timeOptions: ScheduleTimeType[];
  isDateDisabled: boolean;
  isTimeDisabled: boolean;
  onDateChange: (option: ScheduleDateType) => void;
}

/**
 * Builds and keeps scheduled date/time dropdown options in sync with merchant hours,
 * selected daypart, and order estimate context.
 *
 * Scheduling hours are resolved once per render from the merchant's vertical defaults
 * (restaurant service PICKUP/WEBSITE hours, otherwise merchant businessHours), and an
 * optional `businessHours` override wins when provided.
 *
 * @param params Hook inputs for scheduling context and state setters.
 * @param params.scheduleDateRange Number of days ahead that can be scheduled.
 * @param params.businessHours Optional override with explicit semantics:
 * - `Hours` => use the provided schedule hours
 * - `null` => explicit no-hours state (return no schedulable options)
 * - `undefined` => treated as `null` by this hook to avoid silent fallback behavior
 * @param params.merchant Merchant source of timezone, lead time, and busy-mode fields.
 * @param params.selectedDayPart Optional daypart filter for available windows.
 * @param params.orderType Current order type used by scheduling logic.
 * @param params.orderEstimates Optional delivery/pickup estimate context.
 * @param params.scheduleDate Currently selected date option.
 * @param params.scheduleTime Currently selected time option.
 * @param params.setScheduleDate Setter used when date selection is corrected.
 * @param params.setScheduleTime Setter used when time selection is corrected;
 * also advances a stale same-day selection to the first available slot.
 * @returns Date/time options, disabled flags, and an `onDateChange` handler.
 */
export const useScheduledDropdownOptions = ({
  scheduleDateRange,
  businessHours,
  merchant,
  selectedDayPart = null,
  orderType,
  orderEstimates = {},
  scheduleDate,
  scheduleTime,
  setScheduleDate,
  setScheduleTime,
}: UseScheduledDropdownOptionsParams): UseScheduledDropdownOptionsResult => {
  const tPickup = useTranslations("CalculatePickTime");
  const locale = useLocale();
  const { deliveryTime, pickupTime } = orderEstimates;
  const { timezone } = merchant;

  const context = useMemo(
    () =>
      createSchedulingContext({
        merchant,
        locale,
        orderType: orderType as OrderType,
        orderEstimates: {
          deliveryTime,
          pickupTime,
        },
        daypartMenu: selectedDayPart,
        hoursOverride: businessHours ?? null,
      }),
    [
      merchant,
      locale,
      orderType,
      deliveryTime,
      pickupTime,
      selectedDayPart,
      businessHours,
    ]
  );

  const dateOptions: ScheduleDateType[] = useMemo(
    () =>
      getSchedulableDateOptions({
        context,
        scheduleDateRange,
        t: tPickup,
      }),
    [scheduleDateRange, context, tPickup]
  );

  // Built from the exact same context instance as `dateOptions` so the calendar and
  // the dropdown can never disagree about which dates are schedulable.
  const calendarOptions: ScheduledDateCalendarOptions = useMemo(
    () => getSchedulableDateCalendarOptions({ context, scheduleDateRange }),
    [context, scheduleDateRange]
  );

  useEffect(() => {
    if (
      dateOptions.length > 0 &&
      !dateOptions.some((option) => {
        if (!scheduleDate?.dateObject) {
          return false;
        }

        const selectedDate = DateTime.fromJSDate(
          toNativeDate(scheduleDate.dateObject),
          {
            zone: timezone,
          }
        );
        const optionDate = DateTime.fromJSDate(option.dateObject, {
          zone: timezone,
        });

        return (
          option.displayValue === scheduleDate?.displayValue &&
          optionDate.hasSame(selectedDate, "day")
        );
      })
    ) {
      setScheduleDate(dateOptions[0]);
    }
  }, [dateOptions, scheduleDate, setScheduleDate, timezone]);

  const timeOptions: ScheduleTimeType[] = useMemo(() => {
    const rawDate = scheduleDate?.dateObject;
    if (!rawDate) {
      return [];
    }

    return getTimeOptionsForDate({
      dateObject: toNativeDate(rawDate),
      context,
    });
  }, [scheduleDate, context]);

  useEffect(() => {
    if (timeOptions.length === 0) return;

    // Slot identity = label + merchant-zone day. Comparing exact instants breaks
    // across browser timezones; a stale slot still resets because its label drops out.
    const toMerchantTime = (d: Date) =>
      DateTime.fromJSDate(toNativeDate(d), { zone: timezone });

    const isSelectedTimeAvailable =
      !!scheduleTime?.displayValue &&
      !!scheduleTime?.timeObject &&
      timeOptions.some(
        (option) =>
          option.displayValue === scheduleTime.displayValue &&
          toMerchantTime(option.timeObject).hasSame(
            toMerchantTime(scheduleTime.timeObject),
            "day"
          )
      );

    if (!isSelectedTimeAvailable) {
      setScheduleTime(timeOptions[0]);
    }
  }, [scheduleTime, setScheduleTime, timeOptions, timezone]);

  const onDateChange = useCallback(
    (option: ScheduleDateType) => {
      setScheduleDate(option);
      setScheduleTime({
        displayValue: "",
        timeObject: option.dateObject,
      });
    },
    [setScheduleDate, setScheduleTime]
  );

  return {
    dateOptions,
    calendarOptions,
    timeOptions,
    isDateDisabled: dateOptions.length === 0,
    isTimeDisabled: timeOptions.length === 0,
    onDateChange,
  };
};
